import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, Thermometer, Flame, Wind, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_PAGES = {
  '/schedule-ac-repair': {
    title: 'Schedule AC Repair',
    subtitle: 'Get fast, reliable air conditioning repair from trusted local technicians.',
    icon: Thermometer,
    iconBg: 'bg-[#E0F2FE]',
    iconColor: 'text-[#0EA5E9]',
    serviceType: 'ac-repair',
    leadTag: 'repair_lead',
    overview: 'When your AC breaks down in a Louisiana summer, you need help fast. Our network of licensed, insured HVAC contractors provides same-day and next-day AC repair across the Greater New Orleans area. From refrigerant leaks to compressor failures, we connect you with professionals who can diagnose and fix the problem quickly.',
    includes: [
      'Diagnostic inspection of your AC system',
      'Refrigerant level check and recharge',
      'Thermostat testing and calibration',
      'Electrical component inspection',
      'Condenser and evaporator coil cleaning',
      'Blower motor and fan assessment',
      'Air filter replacement',
      'Full system performance test after repair',
    ],
  },
  '/schedule-heating-service': {
    title: 'Schedule Heating Service',
    subtitle: 'Expert furnace repair, heat pump service, and heating system maintenance.',
    icon: Flame,
    iconBg: 'bg-orange-50',
    iconColor: 'text-[#F97316]',
    serviceType: 'heating-repair',
    leadTag: 'heating_lead',
    overview: 'Even in Louisiana, reliable heating is essential during cold snaps and winter months. Whether you have a furnace, heat pump, or boiler, our contractor network provides expert heating repair and installation services. Don\'t wait until a cold front hits — schedule service now.',
    includes: [
      'Furnace inspection and repair',
      'Heat pump diagnostics and servicing',
      'Thermostat calibration for heating mode',
      'Gas line and safety valve inspection',
      'Heat exchanger examination',
      'Pilot light and ignition system check',
      'Ductwork inspection for heat loss',
      'System efficiency evaluation',
    ],
  },
  '/request-duct-cleaning': {
    title: 'Request Duct Cleaning',
    subtitle: 'Improve indoor air quality and HVAC efficiency with professional duct cleaning.',
    icon: Wind,
    iconBg: 'bg-[#E0F2FE]',
    iconColor: 'text-[#0EA5E9]',
    serviceType: 'duct-cleaning',
    leadTag: 'cleaning_lead',
    overview: 'Louisiana\'s humidity creates ideal conditions for mold, dust, and allergens to accumulate in your ductwork. Professional duct cleaning removes contaminants, improves airflow, and can significantly reduce allergy symptoms. It also helps your HVAC system run more efficiently, lowering energy bills.',
    includes: [
      'Complete inspection of all ductwork',
      'High-powered vacuum extraction of dust and debris',
      'Mold and mildew treatment',
      'Sanitization of supply and return ducts',
      'Vent cover cleaning',
      'Dryer vent cleaning (upon request)',
      'Before and after airflow testing',
      'Written report of findings',
    ],
  },
  '/maintenance-plans': {
    title: 'HVAC Maintenance Plans',
    subtitle: 'Preventive maintenance to extend your system\'s life and prevent costly breakdowns.',
    icon: Wrench,
    iconBg: 'bg-orange-50',
    iconColor: 'text-[#F97316]',
    serviceType: 'maintenance',
    leadTag: 'maintenance_lead',
    overview: 'Regular maintenance is the single best way to extend the life of your HVAC system and avoid unexpected repair bills. A professional maintenance plan typically includes two visits per year — one before cooling season and one before heating season — to keep your system running at peak efficiency.',
    includes: [
      'Bi-annual system tune-ups (spring and fall)',
      'Priority scheduling for repairs',
      'Discounted repair rates for plan members',
      'Refrigerant level monitoring',
      'Electrical connection tightening',
      'Condensate drain cleaning',
      'Air filter replacement at each visit',
      'Written system health report',
    ],
  },
};

const ServiceRequestPage = () => {
  const location = useLocation();
  const data = SERVICE_PAGES[location.pathname];
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    service_needed: data?.serviceType || '',
    message: '',
  });

  if (!data) {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        service_type: formData.service_needed,
        city: formData.city,
        lead_tag: data.leadTag,
        message: formData.message || '',
      });
      setSubmitted(true);
      toast.success('Your request has been received.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const IconComponent = data.icon;

  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className={`w-16 h-16 ${data.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
            <IconComponent className={`w-8 h-8 ${data.iconColor}`} />
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="service-page-heading"
          >
            {data.title}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {data.subtitle}
          </p>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Service Overview
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">{data.overview}</p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What This Service Includes
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.includes.map((item) => (
              <div key={item} className="flex items-start gap-3 p-3 bg-white rounded-xl" data-testid={`service-include-${item.slice(0, 15).replace(/\s/g, '-')}`}>
                <CheckCircle className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#0F172A]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section className="py-16" id="service-form">
        <div className="max-w-xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="confirmation-heading">
                  Your request has been received.
                </h3>
                <p className="text-slate-500 mb-6" data-testid="confirmation-message">
                  A trusted local HVAC professional will contact you shortly.
                </p>
                <a href="/">
                  <Button variant="outline" className="rounded-full">
                    Back to Home
                  </Button>
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Request Service
                  </h2>
                  <p className="text-slate-500 mt-1">Fill out the form and we'll connect you with a local pro</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 font-medium">Name *</Label>
                    <Input
                      id="name" name="name" type="text" placeholder="John Smith"
                      value={formData.name} onChange={handleInputChange}
                      className="mt-1.5 h-12 border-slate-200 rounded-lg"
                      data-testid="service-form-name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">Phone *</Label>
                    <Input
                      id="phone" name="phone" type="tel" placeholder="(555) 123-4567"
                      value={formData.phone} onChange={handleInputChange}
                      className="mt-1.5 h-12 border-slate-200 rounded-lg"
                      data-testid="service-form-phone"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email *</Label>
                  <Input
                    id="email" name="email" type="email" placeholder="john@example.com"
                    value={formData.email} onChange={handleInputChange}
                    className="mt-1.5 h-12 border-slate-200 rounded-lg"
                    data-testid="service-form-email"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="city" className="text-slate-700 font-medium">City *</Label>
                  <Select value={formData.city} onValueChange={(v) => setFormData(prev => ({ ...prev, city: v }))}>
                    <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg" data-testid="service-form-city">
                      <SelectValue placeholder="Select your city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New Orleans">New Orleans</SelectItem>
                      <SelectItem value="Metairie">Metairie</SelectItem>
                      <SelectItem value="Kenner">Kenner</SelectItem>
                      <SelectItem value="Slidell">Slidell</SelectItem>
                      <SelectItem value="Chalmette">Chalmette</SelectItem>
                      <SelectItem value="Covington">Covington</SelectItem>
                      <SelectItem value="Mandeville">Mandeville</SelectItem>
                      <SelectItem value="Hammond">Hammond</SelectItem>
                      <SelectItem value="Gretna">Gretna</SelectItem>
                      <SelectItem value="Marrero">Marrero</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="service_needed" className="text-slate-700 font-medium">Service Needed</Label>
                  <Select value={formData.service_needed} onValueChange={(v) => setFormData(prev => ({ ...prev, service_needed: v }))}>
                    <SelectTrigger className="mt-1.5 h-12 border-slate-200 rounded-lg" data-testid="service-form-service">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ac-repair">AC Repair</SelectItem>
                      <SelectItem value="ac-installation">AC Installation</SelectItem>
                      <SelectItem value="heating-repair">Heating Repair</SelectItem>
                      <SelectItem value="heating-installation">Heating Installation</SelectItem>
                      <SelectItem value="duct-cleaning">Duct Cleaning</SelectItem>
                      <SelectItem value="maintenance">Maintenance Plan</SelectItem>
                      <SelectItem value="emergency">Emergency Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 font-medium">Message (optional)</Label>
                  <Textarea
                    id="message" name="message"
                    placeholder="Describe your issue or what you need..."
                    value={formData.message} onChange={handleInputChange}
                    className="mt-1.5 border-slate-200 rounded-lg min-h-[80px]"
                    data-testid="service-form-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 rounded-full text-lg"
                  data-testid="service-form-submit"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Service'}
                  {!isSubmitting && <ArrowRight className="w-5 h-5 ml-2" />}
                </Button>

                <p className="text-center text-xs text-slate-400">
                  Your information is secure and never shared without your consent.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-slate-600">
            Need a full system replacement instead?{' '}
            <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9] hover:underline font-medium">
              Get an instant HVAC replacement estimate
            </a>.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceRequestPage;
